import { request } from '@ns/http-request';
import { z } from 'zod';

import type { GraphListOptions, GraphListResult, GraphVersion } from './types';

type DirectoryItemSchema = z.ZodTypeAny;

/** Runs one fixed collection request and binds a validated continuation when Graph supplies one. */
export async function listDirectory<TSchema extends DirectoryItemSchema>(
  options: GraphListOptions,
  collection: 'groups' | 'servicePrincipals',
  itemSchema: TSchema,
  continuation?: URL,
  discoveredBase?: URL,
): Promise<GraphListResult<z.output<TSchema>>> {
  const base =
    discoveredBase ??
    new URL(await options.discoveryApi.getBaseUrl('msgraph-proxy'));
  const path = `${trimTrailingSlashes(base.pathname)}/${
    options.version
  }/${collection}`;
  const url = continuation ?? new URL(path, base.origin);
  if (!continuation) setQueryString(url, options);
  const { getAssertion } = options;
  const assertion = getAssertion === null ? undefined : await getAssertion();
  // A supplier that resolves to anything but a non-blank string is broken, and
  // treating its result as "no header" would hide that until the proxy refuses.
  if (
    getAssertion !== null &&
    (typeof assertion !== 'string' || assertion.trim() === '')
  ) {
    throw new Error('Graph assertion supplier returned an empty assertion');
  }
  const envelopeSchema = z
    .object({
      '@odata.context': z.string().optional(),
      '@odata.count': z.number().optional(),
      '@odata.nextLink': z.string().optional(),
      value: z.array(itemSchema),
    })
    .passthrough();
  const result = await request({
    fetch: options.fetch,
    headers: {
      ...(options.consistencyLevel
        ? { ConsistencyLevel: options.consistencyLevel }
        : {}),
      ...(assertion === undefined ? {} : { 'ns-entra-token': assertion }),
    },
    method: 'GET',
    signal: options.signal,
    url: url.toString(),
    ...options.resilience,
    validate: {
      response: raw => {
        const envelope = envelopeSchema.parse(raw);

        // Validate here rather than after `request` returns so an untrusted
        // continuation is reported through the shared validation-error shape.
        parseNextLink(
          envelope['@odata.nextLink'],
          base,
          options.version,
          collection,
        );

        return envelope;
      },
    },
  });
  const body = result.body;
  const next = parseNextLink(
    body['@odata.nextLink'],
    base,
    options.version,
    collection,
  );
  return {
    response: body,
    ...(next
      ? {
          more: () =>
            listDirectory(options, collection, itemSchema, next, base),
        }
      : {}),
  };
}

function setQueryString(url: URL, options: GraphListOptions): void {
  const values: readonly [string, string | number | boolean | undefined][] = [
    ['$count', options.count],
    ['$expand', options.expand],
    ['$filter', options.filter],
    ['$orderby', options.orderby],
    ['$search', options.search],
    ['$select', options.select],
    ['$top', options.top],
  ];
  const query = values
    .filter(
      (entry): entry is [string, string | number | boolean] =>
        entry[1] !== undefined,
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
    .join('&');

  if (query !== '') url.search = query;
}

function parseNextLink(
  raw: string | undefined,
  base: URL,
  version: GraphVersion,
  collection: 'groups' | 'servicePrincipals',
): URL | undefined {
  if (raw === undefined) return undefined;
  if (raw.trim() === '')
    throw new Error('Graph continuation must not be empty');
  let next: URL;
  try {
    next = new URL(raw, base);
  } catch {
    throw new Error('Graph continuation is not a URL');
  }
  const expected = `${trimTrailingSlashes(
    base.pathname,
  )}/${version}/${collection}`;
  if (
    next.origin !== base.origin ||
    next.username ||
    next.password ||
    next.hash ||
    next.pathname !== expected
  ) {
    throw new Error('Graph continuation leaves its bound proxy route');
  }
  return next;
}

function trimTrailingSlashes(path: string): string {
  let end = path.length;
  while (end > 0 && path.at(end - 1) === '/') end -= 1;
  return path.slice(0, end);
}
